import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { getUsersFromSameTeam, getTeamMemberTotalsByLeague } from '../../Api/authApi.js.js';
import { formatPrice } from '../../functions/price-formater.js';
import StackedBarChart from "../../components/chartgraphique/BarChart/index.js";
import Navbarfooter from "../../components/Navbarfooter/index.js";
import './classement.css';

const PALETTE = ['#7AB8E0', '#9B7BE0', '#E0C281', '#7AE0C5', '#E07AAB', '#4ADE9E', '#FF7A78'];

const nameColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return PALETTE[h % PALETTE.length];
};

const initials = (name = '') =>
  name.split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';

const fmtPct = (n) => {
  const v = parseFloat(n) || 0;
  return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

const Classement = () => {
  const [scope, setScope] = useState('general');
  const userId = useSelector((state) => state.auth.user.id);

  const [teamMembers, setTeamMembers] = useState({});
  const [teamname, setTeamname] = useState('');
  const [teamRanking, setTeamRanking] = useState([]);
  const [individualRanking, setIndividualRanking] = useState([]);

  useEffect(() => {
    getUsersFromSameTeam(userId)
      .then(res => {
        setTeamMembers(res.team_members || {});
        setTeamname(res.team_name || '');
      })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    getTeamMemberTotalsByLeague(userId)
      .then(res => {
        setTeamRanking(res.teams || []);
        setIndividualRanking(res.individualRanking || []);
      })
      .catch(() => {});
  }, [userId]);

  const rankedTeams = [...teamRanking].sort(
    (a, b) => (parseFloat(b.total_montant) || 0) - (parseFloat(a.total_montant) || 0)
  );
  const myTeamRank = rankedTeams.findIndex(t => t.team_name === teamname) + 1;
  const myTeam = rankedTeams.find(t => t.team_name === teamname);

  const top3Teams = rankedTeams.slice(0, 3);
  const top3Players = individualRanking.slice(0, 3);
  const myPlayer = individualRanking.find(p => p.team_name === teamname);

  const membersArray = Object.values(teamMembers);

  return (
    <div className="classement-page">

      {/* Header */}
      <div className="classement-header" style={{ padding: '4px 4px 14px' }}>
        <div style={{ color: 'var(--text-mute)', fontSize: 13, fontWeight: 500 }}>Classement</div>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>
          {scope === 'general' ? 'Top traders' : 'Top équipes'}
        </h1>
      </div>

      {/* Scope tabs */}
      <div id="classement-tabs" className="classement-tabs">
        {[
          { id: 'general', label: 'Général'  },
          { id: 'equipe',  label: 'Équipe'   },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setScope(t.id)}
            className="classement-tab-btn"
            style={{
              background: scope === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
              color: scope === t.id ? 'var(--text)' : 'var(--text-dim)',
              boxShadow: scope === t.id ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'none',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Général : classement individuel */}
      {scope === 'general' && (
        <>
          {/* Podium top 3 joueurs */}
          <div id="classement-podium" className="relief classement-podium-wrap">
            <div className="classement-podium-glow" />
            <div className="classement-podium-grid">
              <PodiumStep entry={top3Players[1]} place={2} height={72} />
              <PodiumStep entry={top3Players[0]} place={1} height={92} />
              <PodiumStep entry={top3Players[2]} place={3} height={58} />
            </div>
          </div>

          {/* Ma position */}
          {myPlayer && (
            <div className="relief classement-me-card">
              <NameAvatar name={myPlayer.username} size={32} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Vous
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  #{myPlayer.rank} · {myPlayer.username}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {myPlayer.capital != null && (
                  <span className="num" style={{ fontSize: 11, fontWeight: 600 }}>
                    {formatPrice(myPlayer.capital)}
                  </span>
                )}
                <PctBadge pct={myPlayer.pct} />
              </div>
            </div>
          )}

          {/* Liste complète */}
          <div className="classement-section-header">
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Classement général</h2>
            <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>{individualRanking.length} traders</span>
          </div>
          <div className="relief" style={{ borderRadius: 18, padding: '4px 14px' }}>
            {individualRanking.map(player => (
              <PlayerRow
                key={player.user_id}
                player={player}
                isMe={player.team_name === teamname}
              />
            ))}
          </div>
        </>
      )}

      {/* ── Équipe : classement par équipe */}
      {scope === 'equipe' && (
        <>
          {/* Podium top 3 équipes */}
          <div id="classement-podium" className="relief classement-podium-wrap">
            <div className="classement-podium-glow" />
            <div className="classement-podium-grid">
              <PodiumStep entry={top3Teams[1]} place={2} height={72} teamMode />
              <PodiumStep entry={top3Teams[0]} place={1} height={92} teamMode />
              <PodiumStep entry={top3Teams[2]} place={3} height={58} teamMode />
            </div>
          </div>

          {/* Mon équipe */}
          {myTeam && (
            <div className="relief classement-me-card">
              <NameAvatar name={teamname} size={32} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Votre équipe
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>#{myTeamRank} · {teamname}</div>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <PctBadge pct={parseFloat(myTeam.total_montant)} />
              </div>
            </div>
          )}

          {/* Liste équipes */}
          <div className="classement-section-header">
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>Classement équipes</h2>
            <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>{rankedTeams.length} équipes</span>
          </div>
          <div className="relief" style={{ borderRadius: 18, padding: '4px 14px' }}>
            {rankedTeams.map((team, index) => (
              <TeamRow
                key={team.team_id}
                team={team}
                rank={index + 1}
                isMe={team.team_name === teamname}
              />
            ))}
          </div>

          Membres de l'équipe + graphique
          {membersArray.length > 0 && (
            <div className="relief" style={{ marginTop: 14, borderRadius: 16, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                Membres de votre équipe
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                {membersArray.map((member, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <NameAvatar name={member.user_name} size={44} />
                    <span style={{
                      fontSize: 10, color: 'var(--text-dim)', fontWeight: 500,
                      maxWidth: 56, textAlign: 'center',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {member.user_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Navbarfooter />
    </div>
  );
};

/* ── Shared components ── */

function NameAvatar({ name = '', size = 32, ring }) {
  const color = nameColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: `linear-gradient(180deg, ${color}44 0%, ${color}18 100%)`,
      color,
      border: ring ? `2px solid ${ring}` : '1px solid var(--border)',
      display: 'grid', placeItems: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.36),
      boxShadow: ring ? `0 0 0 3px ${ring}22` : 'none',
      flexShrink: 0,
      letterSpacing: '0.01em',
    }}>{initials(name)}</div>
  );
}

function PctBadge({ pct }) {
  const v = parseFloat(pct) || 0;
  return (
    <span className="num" style={{
      fontSize: 11, fontWeight: 700,
      color: v >= 0 ? 'var(--up)' : 'var(--down)',
      background: v >= 0 ? 'var(--up-soft)' : 'var(--down-soft)',
      padding: '3px 7px', borderRadius: 6,
    }}>{fmtPct(v)}</span>
  );
}

function PodiumStep({ entry, place, height, teamMode = false }) {
  const colors = { 1: '#E0C281', 2: '#B8C2D6', 3: '#C99373' };
  const c = colors[place];
  const isEmpty = !entry;
  const name = teamMode ? entry?.team_name : entry?.username;
  const pct = teamMode ? parseFloat(entry?.total_montant) : parseFloat(entry?.pct);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {place === 1 && (
        <svg width="20" height="20" viewBox="0 0 20 20" style={{ marginBottom: -4, opacity: isEmpty ? 0.25 : 1 }}>
          <path d="M3 6l3 3 4-5 4 5 3-3v9H3z" fill={c} />
        </svg>
      )}
      {isEmpty ? (
        <div style={{
          width: place === 1 ? 56 : 44, height: place === 1 ? 56 : 44,
          borderRadius: '50%',
          border: `2px dashed ${c}44`,
          flexShrink: 0,
        }} />
      ) : (
        <NameAvatar name={name} size={place === 1 ? 56 : 44} ring={c} />
      )}
      <div style={{ textAlign: 'center', minHeight: place === 1 ? 34 : 30 }}>
        {!isEmpty && (
          <>
            <div style={{
              fontSize: place === 1 ? 13 : 11, fontWeight: 600, lineHeight: 1.2,
              maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {name}
            </div>
            <div className="num" style={{ fontSize: 11, color: (pct || 0) >= 0 ? 'var(--up)' : 'var(--down)', marginTop: 1 }}>
              {fmtPct(pct || 0)}
            </div>
          </>
        )}
      </div>
      <div style={{
        width: '100%', height,
        background: isEmpty ? `rgba(255,255,255,0.02)` : `linear-gradient(180deg, ${c}22 0%, ${c}00 100%)`,
        border: isEmpty ? `1px dashed ${c}22` : `1px solid ${c}33`,
        borderRadius: '10px 10px 0 0',
        marginTop: 4,
        display: 'grid', placeItems: 'center',
      }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: isEmpty ? `${c}44` : c, letterSpacing: '-0.02em' }}>{place}</span>
      </div>
    </div>
  );
}

function PlayerRow({ player, isMe }) {
  const pct = parseFloat(player.pct) || 0;
  return (
    <div
      className="classement-team-row"
      style={{
        display: 'grid', gridTemplateColumns: '24px 32px 1fr auto auto', gap: 10, alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        background: isMe ? 'linear-gradient(90deg, rgba(74,222,158,0.13) 0%, rgba(255,255,255,0.05) 100%)' : 'transparent',
        margin: isMe ? '0 -10px' : 0,
        padding: isMe ? '10px 10px' : '10px 0',
        borderRadius: isMe ? 13 : 0,
      }}
    >
      <span className="num" style={{
        fontSize: 12, fontWeight: 700,
        color: player.rank <= 3 ? 'var(--gold)' : 'var(--text-mute)',
      }}>{player.rank}</span>
      <NameAvatar name={player.username} size={32} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {player.username}
          {isMe && <span style={{ fontSize: 10, color: 'var(--up)', marginLeft: 6 }}>VOUS</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>{player.team_name}</div>
      </div>
      {player.capital != null && (
        <div className="num" style={{ fontSize: 11, fontWeight: 600, textAlign: 'right' }}>
          {formatPrice(player.capital)}
        </div>
      )}
      <div className="num" style={{
        fontSize: 11, fontWeight: 600,
        color: pct >= 0 ? 'var(--up)' : 'var(--down)',
        minWidth: 48, textAlign: 'right',
      }}>{fmtPct(pct)}</div>
    </div>
  );
}

function TeamRow({ team, rank, isMe }) {
  const pct = parseFloat(team.total_montant) || 0;
  return (
    <div
      className="classement-team-row"
      style={{
        display: 'grid', gridTemplateColumns: '24px 32px 1fr auto', gap: 10, alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        background: isMe ? 'linear-gradient(90deg, rgba(74,222,158,0.13) 0%, rgba(255,255,255,0.05) 100%)' : 'transparent',
        margin: isMe ? '0 -10px' : 0,
        padding: isMe ? '10px 10px' : '10px 0',
        borderRadius: isMe ? 13 : 0,
      }}
    >
      <span className="num" style={{
        fontSize: 12, fontWeight: 700,
        color: rank <= 3 ? 'var(--gold)' : 'var(--text-mute)',
      }}>{rank}</span>
      <NameAvatar name={team.team_name} size={32} />
      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {team.team_name}
        {isMe && <span style={{ fontSize: 10, color: 'var(--up)', marginLeft: 6 }}>VOUS</span>}
      </div>
      <div className="num" style={{
        fontSize: 12, fontWeight: 600,
        color: pct >= 0 ? 'var(--up)' : 'var(--down)',
        minWidth: 60, textAlign: 'right',
      }}>{fmtPct(pct)}</div>
    </div>
  );
}

export default Classement;
